import { useMemo } from "react";
import { useGetList, type Identifier } from "ra-core";
import type { Deal } from "@/components/atomic-crm/types";
import { useContactNames } from "@/modules/clients/ClientContactTag";
import {
  clientDealFilter,
  type ClientScope,
} from "@/modules/clients/clientScope";
import { RecordThreadsPanel } from "@/modules/threads/RecordThreadsPanel";
import {
  contactThreadTarget,
  dealThreadTarget,
} from "@/modules/threads/threadTargets";

/**
 * Account / contact threads: the people's own threads plus every thread of
 * their projects. New threads go to the primary person or a project.
 */
export const ClientThreadsTab = ({
  scope,
  contactIds,
  primaryContactId,
}: {
  scope: ClientScope;
  contactIds: Identifier[];
  primaryContactId?: Identifier | null;
}) => {
  const { data: deals = [] } = useGetList<Deal>(
    "deals",
    {
      filter: clientDealFilter(scope),
      pagination: { page: 1, perPage: 100 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { staleTime: 60_000 },
  );
  const names = useContactNames(contactIds);

  const targets = useMemo(() => {
    const people = [...contactIds]
      .sort((a, b) =>
        String(a) === String(primaryContactId)
          ? -1
          : String(b) === String(primaryContactId)
            ? 1
            : 0,
      )
      .map((id) =>
        contactThreadTarget(id, names.get(String(id)) ?? `Contact #${id}`),
      );
    const projects = deals.map((deal) =>
      dealThreadTarget(deal.id, deal.name?.trim() || `Project #${deal.id}`),
    );
    return [...people, ...projects];
  }, [contactIds, primaryContactId, names, deals]);

  const scopeIds = useMemo(
    () => ({ contactIds, dealIds: deals.map((deal) => deal.id) }),
    [contactIds, deals],
  );

  return (
    <RecordThreadsPanel
      scope={scopeIds}
      targets={targets}
      defaultTargetKey={
        primaryContactId != null ? `contact:${primaryContactId}` : undefined
      }
    />
  );
};
